import { useCallback, useMemo, useState } from "react";

import { Button, Card, Icon, Input, Notification, Select } from "@stellar/design-system";

import { useMultisigAuditLogs } from "@/apiQueries/useMultisigAuditLogs";
import { useMultisigAuditLogExport } from "@/apiQueries/useMultisigAuditLogExport";
import { ErrorWithExtras } from "@/components/ErrorWithExtras";
import { InfoTooltip } from "@/components/InfoTooltip";
import { Pagination } from "@/components/Pagination";
import { Table } from "@/components/Table";
import { AssetAmount } from "@/components/AssetAmount";
import { formatDateTime } from "@/helpers/formatIntlDateTime";
import { MultisigAuditLogSearchParams, MultisigAuditSortField, MultisigTxType, SortDirection } from "@/types";

import "./styles.scss";

const PAGE_LIMIT = 20;

const DEFAULT_SORT_FIELD: MultisigAuditSortField = "created_at";
const DEFAULT_SORT_DIRECTION: Extract<SortDirection, "asc" | "desc"> = "desc";

const TX_TYPE_OPTIONS: Array<{ value: "" | MultisigTxType; label: string }> = [
  { value: "", label: "All transaction types" },
  { value: "ENROLLMENT", label: "Enrollment" },
  { value: "UNENROLLMENT", label: "Unenrollment" },
  { value: "ADD_SIGNER", label: "Add signer" },
  { value: "REMOVE_SIGNER", label: "Remove signer" },
  { value: "DISBURSEMENT", label: "Disbursement" },
];

const shortenHash = (value: string) => {
  if (!value) return "";
  if (value.length <= 16) return value;
  return `${value.slice(0, 8)}...${value.slice(-8)}`;
};

export const MultisigAuditCard = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<MultisigAuditSortField>(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState<Extract<SortDirection, "asc" | "desc">>(DEFAULT_SORT_DIRECTION);
  const [txType, setTxType] = useState<"" | MultisigTxType>("");
  const [signerKey, setSignerKey] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const searchParams: MultisigAuditLogSearchParams = useMemo(() => {
    return {
      page: String(currentPage),
      page_limit: String(PAGE_LIMIT),
      tx_type: txType || undefined,
      signer_key: signerKey.trim() || undefined,
      start_date: fromDate ? `${fromDate}T00:00:00.000Z` : undefined,
      end_date: toDate ? `${toDate}T23:59:59.999Z` : undefined,
      sort_by: sortBy,
      sort_direction: sortDirection,
    };
  }, [currentPage, txType, signerKey, fromDate, toDate, sortBy, sortDirection]);

  const { data, isLoading, isFetching, error } = useMultisigAuditLogs(searchParams);
  const {
    mutateAsync: exportCsv,
    isPending: isExportPending,
    error: exportError,
  } = useMultisigAuditLogExport();

  const rows = data?.data ?? [];
  const maxPages = Math.max(1, data?.pagination?.pages ?? 1);

  const hasFilters = Boolean(txType || signerKey.trim() || fromDate || toDate);
  const hasNonDefaultSort = sortBy !== DEFAULT_SORT_FIELD || sortDirection !== DEFAULT_SORT_DIRECTION;
  const canReset = hasFilters || hasNonDefaultSort || currentPage !== 1;
  const isEmpty = !isLoading && !isFetching && !error && rows.length === 0 && !hasFilters;

  const onSort = useCallback((field: MultisigAuditSortField) => {
    setCurrentPage(1);

    if (sortBy === field) {
      setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
      return;
    }

    setSortBy(field);
    setSortDirection(DEFAULT_SORT_DIRECTION);
  }, [sortBy]);

  const getColumnSortDirection = useCallback((field: MultisigAuditSortField): SortDirection => {
    return sortBy === field ? sortDirection : "default";
  }, [sortBy, sortDirection]);

  if (isEmpty) {
    return null;
  }

  return (
    <Card>
      <div className="CardStack__card MultisigAuditCard">
        <div className="CardStack__title">
          <InfoTooltip infoText="Review multisig transaction audit entries and filter by transaction type, signer, and date window.">
            Multisig Audit Log
          </InfoTooltip>
        </div>

        <div className="MultisigAuditCard__filters">
          <Select
            id="multisig-audit-tx-type"
            fieldSize="sm"
            label="Transaction type"
            value={txType}
            onChange={(e) => {
              setTxType(e.target.value as "" | MultisigTxType);
              setCurrentPage(1);
            }}
          >
            {TX_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>

          <Input
            id="multisig-audit-signer-key"
            label="Signer key"
            fieldSize="sm"
            placeholder="G..."
            value={signerKey}
            onChange={(e) => {
              setSignerKey(e.target.value);
              setCurrentPage(1);
            }}
          />

          <Input
            id="multisig-audit-from-date"
            label="From date"
            fieldSize="sm"
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setCurrentPage(1);
            }}
          />

          <Input
            id="multisig-audit-to-date"
            label="To date"
            fieldSize="sm"
            type="date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="MultisigAuditCard__actions">
          <Button
            size="md"
            variant="tertiary"
            onClick={() => {
              setTxType("");
              setSignerKey("");
              setFromDate("");
              setToDate("");
              setSortBy(DEFAULT_SORT_FIELD);
              setSortDirection(DEFAULT_SORT_DIRECTION);
              setCurrentPage(1);
            }}
            disabled={!canReset}
          >
            Reset
          </Button>

          <Button
            size="md"
            variant="secondary"
            icon={<Icon.Download01 />}
            onClick={() => exportCsv({ ...searchParams, page: "1" })}
            isLoading={isExportPending}
            disabled={isLoading || isFetching}
          >
            Export CSV
          </Button>
        </div>

        {error ? (
          <Notification variant="error" title="Error" isFilled>
            <ErrorWithExtras appError={error} />
          </Notification>
        ) : null}

        {exportError ? (
          <Notification variant="error" title="Error" isFilled>
            <ErrorWithExtras appError={exportError} />
          </Notification>
        ) : null}

        {rows.length > 0 ? (
          <>
            <Card noPadding>
              <Table isLoading={isLoading || isFetching} isScrollable>
                <Table.Header>
                  <Table.HeaderCell
                    sortDirection={getColumnSortDirection("created_at")}
                    onSort={() => onSort("created_at")}
                  >
                    Created at
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortDirection={getColumnSortDirection("tx_type")}
                    onSort={() => onSort("tx_type")}
                  >
                    Transaction type
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortDirection={getColumnSortDirection("disbursement_amount")}
                    onSort={() => onSort("disbursement_amount")}
                  >
                    Disbursement amount
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortDirection={getColumnSortDirection("xdr_hash")}
                    onSort={() => onSort("xdr_hash")}
                  >
                    XDR hash
                  </Table.HeaderCell>
                  <Table.HeaderCell>Cosigner keys</Table.HeaderCell>
                  <Table.HeaderCell
                    sortDirection={getColumnSortDirection("horizon_tx_hash")}
                    onSort={() => onSort("horizon_tx_hash")}
                  >
                    Horizon tx hash
                  </Table.HeaderCell>
                </Table.Header>

                <Table.Body>
                  {rows.map((row) => (
                    <Table.BodyRow key={row.id}>
                      <Table.BodyCell>{formatDateTime(row.created_at)}</Table.BodyCell>
                      <Table.BodyCell>{row.tx_type}</Table.BodyCell>
                      <Table.BodyCell>
                        <AssetAmount amount={row.disbursement_amount} fallback="-" />
                      </Table.BodyCell>
                      <Table.BodyCell>
                        <span className="MultisigAuditCard__hash" title={row.xdr_hash}>
                          {shortenHash(row.xdr_hash)}
                        </span>
                      </Table.BodyCell>
                      <Table.BodyCell>
                        <span className="MultisigAuditCard__keys" title={row.cosigner_keys.join(", ")}>
                          {row.cosigner_keys.join(", ")}
                        </span>
                      </Table.BodyCell>
                      <Table.BodyCell>
                        <span className="MultisigAuditCard__hash" title={row.horizon_tx_hash}>
                          {shortenHash(row.horizon_tx_hash)}
                        </span>
                      </Table.BodyCell>
                    </Table.BodyRow>
                  ))}
                </Table.Body>
              </Table>
            </Card>

            <div className="MultisigAuditCard__pagination">
              <Pagination
                currentPage={currentPage}
                maxPages={maxPages}
                onSetPage={(page) => setCurrentPage(page)}
                isLoading={isLoading || isFetching}
              />
            </div>
          </>
        ) : null}
      </div>
    </Card>
  );
};
